import { Api } from '../api/root-api';
import { ModuleAccessResponse } from './model';
import { setModuleAccessConfig } from './slice';

interface GetModuleAccessConfigRequest {
  sellerWorkspaceId: string;
}

interface SaveModuleAccessConfigRequest {
  sellerWorkspaceId: string;
  enabledModules: Record<string, string[]>;
}

/**
 * Per-user (team member) module-access request. `userId` targets a specific
 * member; when omitted the backend falls back to the caller's own id (JWT).
 * An admin reading/saving another member's config must pass it.
 */
interface SellerModuleAccessUserConfigRequest {
  sellerWorkspaceId: string;
  userId?: string;
}

interface SaveSellerModuleAccessUserConfigRequest
  extends SellerModuleAccessUserConfigRequest {
  enabledModules: Record<string, string[]>;
}

export const moduleAccessApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    getModuleAccessConfig: builder.query<
      ModuleAccessResponse,
      GetModuleAccessConfigRequest
    >({
      query({ sellerWorkspaceId }) {
        return {
          url: `/admin/module-access/config`,
          method: 'GET',
          params: { sellerWorkspaceId },
        };
      },
    }),
    saveModuleAccessConfig: builder.mutation<
      ModuleAccessResponse,
      SaveModuleAccessConfigRequest
    >({
      query({ sellerWorkspaceId, enabledModules }) {
        return {
          url: `/admin/module-access/config`,
          method: 'POST',
          params: { sellerWorkspaceId },
          body: { enabledModules },
        };
      },
    }),
    // Seller-web-app: read the workspace-level module access config for the
    // logged-in seller and cache it in the moduleAccess slice for nav gating.
    getSellerModuleAccessConfig: builder.query<
      ModuleAccessResponse,
      GetModuleAccessConfigRequest
    >({
      query({ sellerWorkspaceId }) {
        return {
          url: `hub/orgs/api/module-access/config`,
          method: 'GET',
          params: { sellerWorkspaceId },
        };
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            setModuleAccessConfig({
              enabledModules: data?.data?.enabledModules ?? null,
              updatedAt: data?.data?.updatedAt,
            })
          );
        } catch (err) {
          // Fail-open: leave config null so all modules stay visible on error.
          console.log(err, 'getSellerModuleAccessConfigErr');
        }
      },
    }),
    // Seller "My Team": per-user (team member) module access config. userId is
    // resolved from the JWT on the server. Used for Level-2 access (seller sets
    // what a team member can see, capped by the workspace config).
    getSellerModuleAccessUserConfig: builder.query<
      ModuleAccessResponse,
      SellerModuleAccessUserConfigRequest
    >({
      query({ sellerWorkspaceId, userId }) {
        return {
          url: `hub/orgs/api/module-access/users/config`,
          method: 'GET',
          params: { sellerWorkspaceId, ...(userId ? { userId } : {}) },
        };
      },
    }),
    saveSellerModuleAccessUserConfig: builder.mutation<
      ModuleAccessResponse,
      SaveSellerModuleAccessUserConfigRequest
    >({
      query({ sellerWorkspaceId, userId, enabledModules }) {
        return {
          url: `hub/orgs/api/module-access/users/config`,
          method: 'POST',
          params: { sellerWorkspaceId, ...(userId ? { userId } : {}) },
          body: { enabledModules },
        };
      },
    }),
  }),
});

export const {
  useLazyGetModuleAccessConfigQuery,
  useSaveModuleAccessConfigMutation,
  useLazyGetSellerModuleAccessConfigQuery,
  useGetSellerModuleAccessConfigQuery,
  useLazyGetSellerModuleAccessUserConfigQuery,
  useGetSellerModuleAccessUserConfigQuery,
  useSaveSellerModuleAccessUserConfigMutation,
} = moduleAccessApi;
